import { DateTimeUtils } from "@stellar/utils";

/** Prefix for all banner dismissal keys in local storage */
const BANNER_DISMISSAL_KEY_PREFIX = "hide-banner-";
export let BannerIdentifier = /*#__PURE__*/function (BannerIdentifier) {
  /** Identifier for the limit exceeded banner */
  BannerIdentifier["LimitExceeded"] = "constraints-limit";
  return BannerIdentifier;
}({});
/**
 * Generate the local storage key for a banner identifier
 * @param identifier - The unique identifier for the banner
 * @returns The local storage key
 */
function getBannerStorageKey(identifier) {
  return `${BANNER_DISMISSAL_KEY_PREFIX}${identifier}`;
}

/**
 * Check if a banner should be shown based on dismissal state
 * Returns false if the banner was dismissed within the expiration period
 * @param identifier - The unique identifier for the banner
 * @param expirationMs - Time in milliseconds after which the dismissal expires (default: 1 week)
 * @returns True if the banner should be shown, false if it should remain hidden
 */
export function shouldShowBanner(identifier, expirationMs = DateTimeUtils.MILLISECONDS_IN_WEEK) {
  try {
    const key = getBannerStorageKey(identifier);
    const storedValue = localStorage.getItem(key);
    if (!storedValue) {
      return true;
    }
    const state = JSON.parse(storedValue);
    const now = Date.now();
    const timeSinceDismissed = now - state.dismissedAt;

    // If dismissal has expired, remove it from storage and show the banner
    if (timeSinceDismissed > expirationMs) {
      localStorage.removeItem(key);
      return true;
    }

    // Banner was dismissed recently, keep it hidden
    return false;
  } catch {
    // If there's any error reading/parsing, show the banner
    return true;
  }
}

/**
 * Mark a banner as dismissed and store the timestamp in local storage
 * @param identifier - The unique identifier for the banner
 */
export function dismissBanner(identifier) {
  try {
    const key = getBannerStorageKey(identifier);
    const state = {
      dismissedAt: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Clear the dismissal state for a banner (useful for testing or manual resets)
 * @param identifier - The unique identifier for the banner
 */
export function clearBannerDismissal(identifier) {
  try {
    const key = getBannerStorageKey(identifier);
    localStorage.removeItem(key);
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Get the timestamp when a banner was dismissed, or null if not dismissed
 * @param identifier - The unique identifier for the banner
 * @returns The timestamp or null
 */
export function getBannerDismissalTimestamp(identifier) {
  try {
    const key = getBannerStorageKey(identifier);
    const storedValue = localStorage.getItem(key);
    if (!storedValue) {
      return null;
    }
    const state = JSON.parse(storedValue);
    return state.dismissedAt;
  } catch {
    return null;
  }
}